import React, { useState, useEffect } from 'react';
import { useNominaMatricula } from 'hooks/useNominaMatricula';
import { useAuth } from 'contexts/AuthContext';
import { catalogoService } from 'services/usuarioService';
import { configuracionService } from 'services/configuracionService';
import { NominaResponse, ESTADO_NOMINA_LABEL, ESTADO_NOMINA_COLOR, EstadoNomina } from 'types/nomina.types';
import { AñoEscolar, Grado, Seccion } from 'types/usuario.types';
import { documentoService } from 'services/documentoService';
import { exportarPDF } from 'utils/reporteExport';
import { htmlNomina } from 'utils/documentoPdf';
import { httpClient } from 'utils/httpClient';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// NominaMatriculaPage — Gestión de Nóminas de Matrícula
// Flujo: GENERADA → APROBADA → ENVIADA_UGEL
// ============================================================

const SpinnerIcon = () => (
  <svg className="animate-spin w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const NominaMatriculaPage: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.idUsuario ?? 0;
  const esDirector = user?.rol === 'DIRECTOR';

  const [anios, setAnios] = useState<AñoEscolar[]>([]);
  const [grados, setGrados] = useState<Grado[]>([]);
  const [secciones, setSecciones] = useState<Seccion[]>([]);
  const [anioId, setAnioId] = useState<number | null>(null);
  const [nivel, setNivel] = useState('');
  const [gradoId, setGradoId] = useState<number | ''>('');
  const [seccionId, setSeccionId] = useState<number | ''>('');
  const [permiteRectificacion, setPermiteRectificacion] = useState(false);
  const [cargandoCatalogos, setCargandoCatalogos] = useState(true);

  const [loadingPdfId,   setLoadingPdfId]   = useState<number | null>(null);
  const [loadingExcelId, setLoadingExcelId] = useState<number | null>(null);

  const handleDescargarExcel = async (n: NominaResponse) => {
    setLoadingExcelId(n.idNomina);
    try {
      await httpClient.downloadFile(
        `/nominas-matricula/${n.idNomina}/excel`,
        `nomina_${n.gradoNombre}_${n.seccionNombre}_${n.anio}.xlsx`,
      );
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error al descargar el Excel');
    } finally {
      setLoadingExcelId(null);
    }
  };

  const handleDescargarPdf = async (n: NominaResponse) => {
    setLoadingPdfId(n.idNomina);
    try {
      const data = await documentoService.getNomina(n.idNomina);
      exportarPDF(
        `Nómina de Matrícula — ${n.gradoNombre} ${n.seccionNombre} ${n.anio}`,
        htmlNomina(data),
      );
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error al generar el PDF');
    } finally {
      setLoadingPdfId(null);
    }
  };

  // Modal aprobación
  const [modalAprobar, setModalAprobar] = useState<NominaResponse | null>(null);
  const [obsAprobacion, setObsAprobacion] = useState('');

  const { nominas, loading, error, acting, actError, generar, aprobar, enviarUgel, rectificar, recargar } =
    useNominaMatricula(anioId);

  useEffect(() => {
    const cargar = async () => {
      setCargandoCatalogos(true);
      try {
        const [a, g, s, cfg] = await Promise.all([
          catalogoService.getAñosEscolares(),
          catalogoService.getGrados(),
          catalogoService.getSecciones(),
          configuracionService.obtener('NOMINA_PERMITE_RECTIFICACION'),
        ]);
        setAnios(a);
        setGrados(g);
        setSecciones(s);
        setPermiteRectificacion(cfg?.valor === 'true');
        if (a.length > 0) setAnioId(a[0].idAnioEscolar);
      } finally {
        setCargandoCatalogos(false);
      }
    };
    cargar();
  }, []);

  const niveles = Array.from(new Set(grados.map(g => g.nivel))).sort();
  const gradosFiltrados = nivel ? grados.filter(g => g.nivel === nivel) : grados;
  const seccionesFiltradas = secciones;

  const nominasFiltradas = nominas.filter(n => {
    if (nivel !== '' && n.gradoNivel !== nivel) return false;
    if (gradoId !== '' && n.gradoId !== Number(gradoId)) return false;
    if (seccionId !== '' && n.seccionId !== Number(seccionId)) return false;
    return true;
  });

  // Combinaciones grado+sección que NO tienen nómina
  const combinacionesSinNomina: { grado: Grado; seccion: Seccion }[] = [];
  if (gradoId !== '' && seccionId !== '') {
    const tieneNomina = nominas.some(n => n.gradoId === Number(gradoId) && n.seccionId === Number(seccionId));
    if (!tieneNomina) {
      const g = grados.find(g => g.idGrado === Number(gradoId));
      const s = secciones.find(s => s.idSeccion === Number(seccionId));
      if (g && s) combinacionesSinNomina.push({ grado: g, seccion: s });
    }
  }

  const handleGenerar = async (gId: number, sId: number) => {
    if (!anioId) return;
    await generar({ gradoId: gId, seccionId: sId, anioEscolarId: anioId, usuarioId: userId });
  };

  const handleAprobar = async () => {
    if (!modalAprobar) return;
    await aprobar(modalAprobar.idNomina, { directorId: userId, observaciones: obsAprobacion || undefined });
    setModalAprobar(null);
    setObsAprobacion('');
  };

  const handleEnviarUgel = async (id: number) => {
    await enviarUgel(id);
  };

  const handleRectificar = async (id: number) => {
    await rectificar(id);
  };

  const formatFecha = (f: string | null) =>
    f ? new Date(f).toLocaleDateString('es-PE') : '—';

  if (cargandoCatalogos) {
    return <div className="flex justify-center py-20"><SpinnerIcon /></div>;
  }

  return (
    <div className="space-y-5">
      {/* Cabecera */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold">Nóminas de Matrícula</h1>
        <p className="text-blue-200 mt-1">Generación, aprobación y envío a UGEL</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Año Escolar</label>
            <select
              value={anioId ?? ''}
              onChange={e => setAnioId(Number(e.target.value))}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              {anios.map(a => (
                <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Nivel</label>
            <select
              value={nivel}
              onChange={e => { setNivel(e.target.value); setGradoId(''); setSeccionId(''); }}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los niveles</option>
              {niveles.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Grado</label>
            <select
              value={gradoId}
              onChange={e => { setGradoId(e.target.value === '' ? '' : Number(e.target.value)); setSeccionId(''); }}
              disabled={!nivel}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">Todos los grados</option>
              {gradosFiltrados.map(g => (
                <option key={g.idGrado} value={g.idGrado}>{g.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Sección</label>
            <select
              value={seccionId}
              onChange={e => setSeccionId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={gradoId === ''}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">Todas las secciones</option>
              {seccionesFiltradas.map(s => (
                <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Errores */}
      {(error || actError) && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
          {error || actError}
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <h2 className="font-bold text-slate-800">Nóminas</h2>
          <button
            onClick={recargar}
            className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
          >
            Actualizar
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><SpinnerIcon /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {['Grado', 'Sección', 'Nivel', 'Total', 'Estado', 'F. Generación', 'F. Aprobación', 'F. Envío', 'Acciones'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {/* Combinaciones sin nómina */}
                {combinacionesSinNomina.map(({ grado, seccion }) => (
                  <tr key={`sin-${grado.idGrado}-${seccion.idSeccion}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-semibold text-slate-800">{grado.nombre}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{seccion.nombre}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{grado.nivel}</td>
                    <td className="px-4 py-3 text-sm text-slate-400">—</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">Sin nómina</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400">—</td>
                    <td className="px-4 py-3 text-sm text-slate-400">—</td>
                    <td className="px-4 py-3 text-sm text-slate-400">—</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleGenerar(grado.idGrado, seccion.idSeccion)}
                        disabled={acting}
                        className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60"
                      >
                        Generar
                      </button>
                    </td>
                  </tr>
                ))}

                {nominasFiltradas.map(n => (
                  <NominaFila
                    key={n.idNomina}
                    nomina={n}
                    esDirector={esDirector}
                    acting={acting}
                    permiteRectificacion={permiteRectificacion}
                    loadingPdf={loadingPdfId === n.idNomina}
                    loadingExcel={loadingExcelId === n.idNomina}
                    onAprobar={() => { setModalAprobar(n); setObsAprobacion(''); }}
                    onRegenerar={() => handleGenerar(n.gradoId, n.seccionId)}
                    onEnviarUgel={() => handleEnviarUgel(n.idNomina)}
                    onRectificar={() => handleRectificar(n.idNomina)}
                    onDescargar={() => handleDescargarPdf(n)}
                    onDescargarExcel={() => handleDescargarExcel(n)}
                    formatFecha={formatFecha}
                  />
                ))}

                {nominasFiltradas.length === 0 && combinacionesSinNomina.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-slate-400 text-sm">
                      No hay nóminas para los filtros seleccionados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal aprobación */}
      {modalAprobar && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
              <h3 className="text-white font-bold">Aprobar Nómina</h3>
              <p className="text-blue-200 text-xs mt-0.5">
                {modalAprobar.gradoNombre} — Sección {modalAprobar.seccionNombre}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">
                  Observaciones (opcional)
                </label>
                <textarea
                  rows={3}
                  value={obsAprobacion}
                  onChange={e => setObsAprobacion(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Ingresa observaciones si corresponde..."
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-between">
              <button
                onClick={() => setModalAprobar(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleAprobar}
                disabled={acting}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl px-4 py-2 transition-colors disabled:opacity-60"
              >
                {acting && <SpinnerIcon />}
                Aprobar Nómina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface NominaFilaProps {
  nomina: NominaResponse;
  esDirector: boolean;
  acting: boolean;
  permiteRectificacion: boolean;
  loadingPdf: boolean;
  loadingExcel: boolean;
  onAprobar: () => void;
  onRegenerar: () => void;
  onEnviarUgel: () => void;
  onRectificar: () => void;
  onDescargar: () => void;
  onDescargarExcel: () => void;
  formatFecha: (f: string | null) => string;
}

const NominaFila: React.FC<NominaFilaProps> = ({
  nomina, esDirector, acting, permiteRectificacion, loadingPdf, loadingExcel,
  onAprobar, onRegenerar, onEnviarUgel, onRectificar, onDescargar, onDescargarExcel, formatFecha,
}) => {
  const renderAcciones = (estado: EstadoNomina) => {
    switch (estado) {
      case 'GENERADA':
        return (
          <div className="flex gap-1.5 flex-wrap">
            {esDirector && (
              <button onClick={onAprobar} disabled={acting}
                className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60">
                Aprobar
              </button>
            )}
            <button onClick={onRegenerar} disabled={acting}
              className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60">
              Regenerar
            </button>
          </div>
        );
      case 'APROBADA':
        return (
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={onEnviarUgel} disabled={acting}
              className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60">
              Enviar a UGEL
            </button>
            <button onClick={onRegenerar} disabled={acting}
              className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60">
              Regenerar
            </button>
          </div>
        );
      case 'ENVIADA_UGEL':
        return permiteRectificacion ? (
          <button onClick={onRectificar} disabled={acting}
            className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60">
            Rectificar
          </button>
        ) : (
          <span className="text-xs text-slate-400">Sin acciones</span>
        );
      case 'RECTIFICADA':
        return esDirector ? (
          <button onClick={onAprobar} disabled={acting}
            className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60">
            Aprobar
          </button>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <tr className="hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3 text-sm font-semibold text-slate-800">{nomina.gradoNombre}</td>
      <td className="px-4 py-3 text-sm text-slate-600">{nomina.seccionNombre}</td>
      <td className="px-4 py-3 text-sm text-slate-500">{nomina.gradoNivel}</td>
      <td className="px-4 py-3 text-sm font-bold text-slate-800">{nomina.totalMatriculados}</td>
      <td className="px-4 py-3">
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${ESTADO_NOMINA_COLOR[nomina.estado]}`}>
          {ESTADO_NOMINA_LABEL[nomina.estado]}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{formatFecha(nomina.fechaGeneracion)}</td>
      <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{formatFecha(nomina.fechaAprobacion)}</td>
      <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{formatFecha(nomina.fechaEnvio)}</td>
      <td className="px-4 py-3">
        <div className="flex gap-1.5 flex-wrap items-center">
          {renderAcciones(nomina.estado)}
          <button
            onClick={onDescargarExcel}
            disabled={loadingExcel}
            title="Descargar Excel SIAGIE oficial"
            className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60"
          >
            {loadingExcel ? (
              <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v8.614l2.955-3.129a.75.75 0 1 1 1.09 1.03l-4.25 4.5a.75.75 0 0 1-1.09 0l-4.25-4.5a.75.75 0 1 1 1.09-1.03L9.25 12.364V3.75A.75.75 0 0 1 10 3ZM3.5 15.75a.75.75 0 0 1 .75-.75h11.5a.75.75 0 0 1 0 1.5H4.25a.75.75 0 0 1-.75-.75Z" clipRule="evenodd"/>
              </svg>
            )}
            Excel
          </button>
          <button
            onClick={onDescargar}
            disabled={loadingPdf}
            title="Descargar nómina en PDF"
            className="flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors disabled:opacity-60"
          >
            {loadingPdf ? (
              <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                <path fillRule="evenodd" d="M10 3a.75.75 0 0 1 .75.75v8.614l2.955-3.129a.75.75 0 1 1 1.09 1.03l-4.25 4.5a.75.75 0 0 1-1.09 0l-4.25-4.5a.75.75 0 1 1 1.09-1.03L9.25 12.364V3.75A.75.75 0 0 1 10 3ZM3.5 15.75a.75.75 0 0 1 .75-.75h11.5a.75.75 0 0 1 0 1.5H4.25a.75.75 0 0 1-.75-.75Z" clipRule="evenodd"/>
              </svg>
            )}
            PDF
          </button>
        </div>
      </td>
    </tr>
  );
};

export default NominaMatriculaPage;
